import { LDFlagSet } from 'launchdarkly-react-client-sdk';
import { memoize } from 'micro-memoize';

import { CurrentUserCheck } from '@futrli/shared-services/authenticator/auth0/types';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { ExtendedReportList, processAllReports } from '../../../processors/reports/reportsList';
import { currentUserQuery } from '../../../queries/currentUser';
import { liveReportsQuery } from '../../../queries/liveReports';
import { membersQuery } from '../../../queries/members';

export interface ReportsListViewState extends ApiViewState {
  allReports?: ExtendedReportList[];
  currentUser?: CurrentUserCheck;
  dashboardReports?: ExtendedReportList[];
  reports?: ExtendedReportList[];
}

export class ReportsListView extends ApiView {
  private currentUserObs: ReturnType<typeof currentUserQuery>;
  private liveReportsObs: ReturnType<typeof liveReportsQuery>;
  private membersObs: ReturnType<typeof membersQuery>;

  private memoizedProcessAllReports: typeof processAllReports;

  public state: ReportsListViewState = {
    ...getDefaultApiViewState(),
    allReports: [],
    currentUser: undefined,
    dashboardReports: [],
    reports: []
  };

  constructor(workspaceId: string, organisationId: string, isConsolidation = false, private flags: LDFlagSet = {}) {
    super();

    this.currentUserObs = this.addQuery(currentUserQuery());
    this.liveReportsObs = this.addQuery(liveReportsQuery(workspaceId, organisationId, isConsolidation));
    this.membersObs = this.addQuery(membersQuery(workspaceId, organisationId, isConsolidation));

    this.memoizedProcessAllReports = memoize(processAllReports);

    this.updateState();
  }

  // ****************************
  // * Private / Protected API *
  // ****************************
  private get currentUser() {
    return this.getResult(this.currentUserObs).data;
  }

  private get allReports() {
    const reports = this.getResult(this.liveReportsObs).data ?? [];
    const currentUser = this.currentUser;
    const members = this.getResult(this.membersObs).data;

    if (!currentUser || !members) {
      return undefined;
    }

    return this.memoizedProcessAllReports(reports, currentUser, members, this.flags);
  }

  private get reports() {
    return this.allReports?.filter(report => report.orientation !== 'dashboard');
  }

  private get dashboardReports() {
    return this.allReports?.filter(report => report.orientation === 'dashboard');
  }

  protected getNewState(): ReportsListViewState {
    return {
      ...super.getNewState(),
      allReports: this.allReports,
      currentUser: this.currentUser,
      dashboardReports: this.dashboardReports,
      reports: this.reports
    };
  }
}
